import type { DialogueLine, SceneView, ShotView, StoryboardView } from "@dramapie/shared";
import { useEffect, useState, type CSSProperties, type DragEvent } from "react";

import { formatDuration } from "../lib/format";
import {
  groupByScene,
  matchesFilter,
  sceneCover,
  sceneShots,
  totalDuration,
  type ShotFilter,
} from "../lib/view";
import { EmptyState } from "./states";
import { ShotCard, type LibraryChipTarget } from "./ShotCard";

export interface StoryboardEditing {
  disabled?: boolean;
  selectedIds: ReadonlySet<string>;
  /** 当前镜（画布 / 时间线选中）：与多选分开高亮。 */
  activeShotId?: string | null;
  /** 「在画布看」：切到画布并定位该镜（M6）。 */
  onOpenCanvas?: (shotId: string) => void;
  /** 打开首尾帧确认弹层。 */
  onOpenFrames?: (shotId: string) => void;
  onToggleSelect: (shotId: string) => void;
  onDurationCommit: (shotId: string, duration: number) => void;
  onDialogueCommit: (shotId: string, lines: DialogueLine[]) => void;
  onConfirmShot?: (shotId: string) => void;
  onRegenerateShot?: (shotId: string) => void;
  /** 镜头卡上的角色 / 场景 chip → 资料库定位（契约 M5 §1）。 */
  onFocusLibrary?: (target: LibraryChipTarget) => void;
  /** 场次内重排；`orderedIds` 必须与该场现有镜头集合完全一致。 */
  onReorder: (sceneId: string, orderedIds: string[]) => void;
}

const sceneLabel = (scene: SceneView | null): string =>
  scene ? `第 ${scene.order} 场 · ${scene.name}` : "未分场";

function SceneSection({
  scene,
  shots,
  allShots,
  editing,
  index,
  orphans = false,
}: {
  scene: SceneView | null;
  shots: ShotView[];
  allShots: ShotView[];
  editing?: StoryboardEditing;
  index: number;
  orphans?: boolean;
}) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [coverFailed, setCoverFailed] = useState(false);
  const canReorder = Boolean(editing && !orphans && scene);
  const ids = shots.map((shot) => shot.id);
  const headingId = orphans ? "scene-unassigned" : `scene-${scene?.id ?? "unknown"}`;
  const cover = scene ? sceneCover(scene.id, allShots) : null;
  const coverUrl = cover?.keyframe?.url ?? null;
  const sceneDuration = totalDuration(scene ? sceneShots(scene.id, allShots) : allShots);

  // 签名 URL 过期时不要留一个破图：直接回退成「无封面」占位。
  useEffect(() => {
    setCoverFailed(false);
  }, [coverUrl]);

  function move(shotId: string, direction: -1 | 1) {
    if (!editing || !scene) return;
    const from = ids.indexOf(shotId);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= ids.length) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, shotId);
    editing.onReorder(scene.id, next);
  }

  function handleDrop(targetId: string, event: DragEvent<HTMLLIElement>) {
    event.preventDefault();
    if (!editing || !scene) return;
    const transfer = event.dataTransfer;
    const activeId = draggingId ?? (transfer ? transfer.getData("text/plain") : "") ?? "";
    setDraggingId(null);
    if (!activeId || activeId === targetId) return;

    const from = ids.indexOf(activeId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;

    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, activeId);
    editing.onReorder(scene.id, next);
  }

  return (
    <section
      aria-label={sceneLabel(scene)}
      className="dp-scene"
      style={{ "--i": index } as CSSProperties}
    >
      <div className="dp-scene-head">
        <span className="dp-scene-index" aria-hidden="true">
          {String(scene?.order ?? 0).padStart(2, "0")}
        </span>
        <div>
          <h3 id={headingId} className="dp-scene-name">
            {orphans ? "未分场" : (scene?.name ?? "未命名")}
            {scene?.time ? <small>{scene.time}</small> : null}
          </h3>
          <div className="dp-scene-sub">
            <span>
              <b>{shots.length}</b> 镜
            </span>
            <span>
              <b>{formatDuration(sceneDuration)}</b> 本场时长
            </span>
          </div>
        </div>
        <span />
        <div className="dp-scene-cover">
          {coverUrl && !coverFailed ? (
            <img
              src={coverUrl}
              alt={`${scene?.name ?? "未分场"} 场景封面`}
              loading="lazy"
              onError={() => setCoverFailed(true)}
            />
          ) : (
            <span className="dp-scene-cover-empty" aria-hidden="true">
              无封面
            </span>
          )}
        </div>
      </div>

      <ul className="dp-shots">
        {shots.map((shot, shotIndex) => {
          const editable = editing
            ? {
                ...editing,
                selected: editing.selectedIds.has(shot.id),
                onMoveUp: canReorder && shotIndex > 0 ? () => move(shot.id, -1) : undefined,
                onMoveDown:
                  canReorder && shotIndex < shots.length - 1 ? () => move(shot.id, 1) : undefined,
              }
            : undefined;

          return (
            <li
              key={shot.id}
              className={`dp-shot-row${draggingId === shot.id ? " is-dragging" : ""}`}
              draggable={canReorder}
              onDragStart={(event) => {
                if (!canReorder) return;
                setDraggingId(shot.id);
                const transfer = event.dataTransfer;
                if (transfer) {
                  transfer.setData("text/plain", shot.id);
                  transfer.effectAllowed = "move";
                }
              }}
              onDragOver={(event) => {
                if (!canReorder) return;
                event.preventDefault();
                if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
              }}
              onDrop={(event) => handleDrop(shot.id, event)}
              onDragEnd={() => setDraggingId(null)}
            >
              <ShotCard shot={shot} scene={scene} editing={editable} index={shotIndex} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * 故事板：按场次分组，场次内按镜头 order 排序；找不到场次的镜头归入「未分场」。
 * 传入 `editing` 后开放编辑；不传则保持只读形态。
 */
export function Storyboard({
  view,
  editing,
  filter = "all",
}: {
  view: StoryboardView;
  editing?: StoryboardEditing;
  filter?: ShotFilter;
}) {
  if (view.shots.length === 0) {
    return (
      <EmptyState
        title="本集还没有镜头"
        description="故事板生成后，镜头会按场次出现在这里。也可以让右栏 Agent 先写一版分镜。"
      />
    );
  }

  const visible = view.shots.filter((shot) => matchesFilter(shot, filter));
  const groups = groupByScene(view.scenes, visible).filter((group) => group.shots.length > 0);

  if (groups.length === 0) {
    return <EmptyState title="没有符合筛选的镜头" description="换一个状态再看，或切回「全部」。" />;
  }

  return (
    <div>
      {groups.map((group, index) => (
        <SceneSection
          key={group.scene?.id ?? "unassigned"}
          scene={group.scene}
          shots={group.shots}
          allShots={view.shots}
          editing={editing}
          index={index}
          orphans={group.scene === null}
        />
      ))}
    </div>
  );
}
